import { Button } from "@ancestors/components/Button";
import { Container } from "@ancestors/components/Container";
import { ProgressCountdown } from "@ancestors/components/Countdown";
import { useCountdown } from "@ancestors/components/useCountdown";
import { useLocalStorage } from "@ancestors/components/useLocalStorage";
import { useSet } from "@ancestors/components/useSet";
import { cn } from "@ancestors/components/utils";
import type { RouterState } from "@smartphoneorchestra/router";
import { Route, Router, useRouter } from "@smartphoneorchestra/router";
import { useDrag } from "@use-gesture/react";
import confetti from "canvas-confetti";
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { BasicStep, CountdownStep, PrivacyPolicyStep, Step } from "./onboardingSteps";
import { getSteps } from "./onboardingSteps";
import { AppearingTextWithSound } from "~/components/AppearingTextWithSound";
import { playRandomNotification, useRandomNotification } from "~/lib/playRandomNotification";
import { useEventStore } from "~/providers/eventStore/eventStoreContext";
import { useScript } from "~/providers/script/scriptContext";
import { useUser } from "~/providers/user/userContext";
import { Redirect } from "~/router";

// Export only used in docs
// eslint-disable-next-line react-refresh/only-export-components
export const onboardingContext = createContext({
  disabledOptions: new Set<string>(),
  actions: {} as Record<string, () => void>,
});

// eslint-disable-next-line react-refresh/only-export-components
export function clearOnboardingCookies(eventKey: string) {
  localStorage.removeItem(`ancestors:${eventKey}:onboarding`);
  localStorage.removeItem(`ancestors:${eventKey}:onboarding:disabled-options`);
}

// ONBOARDING SCREEN
// -----------------

export default function OnboardingScreen() {
  const { script } = useScript();
  const { eventStore } = useEventStore();
  const { user, currentInterface, updateUser } = useUser();
  const eventKey = eventStore.eventKey;

  const steps = useMemo(() => getSteps(script), [script]);

  // Router state is stored in local storage so the user can continue where they left off if they refresh the page
  const [routerState, setRouterState] = useLocalStorage<RouterState | undefined>(
    `ancestors:${eventKey}:onboarding`,
    undefined,
  );

  const handleRouteChange = useCallback(
    (state: RouterState) => {
      setRouterState(state);
      playRandomNotification();
    },
    [setRouterState],
  );

  // Play notification on first render of the screen
  useRandomNotification();

  // Disabled options are also persisted in local storage
  const [storedDisabledOptions, setStoredDisabledOptions] = useLocalStorage<string[]>(
    `ancestors:${eventKey}:onboarding:disabled-options`,
    [],
  );
  const [disabledOptionsSet, onDisabledOptionsChange] = useSet<string>(storedDisabledOptions);

  onDisabledOptionsChange((set) => {
    setStoredDisabledOptions(Array.from(set));
  });

  // Selfie storage permissions
  const handleSelfieStoragePermission = useCallback(
    (agreed: boolean) => {
      updateUser({ agreedToSelfieStorage: agreed });
    },
    [updateUser],
  );

  const contextValue = useMemo(
    () => ({
      disabledOptions: disabledOptionsSet,
      actions: {
        agreeToSelfieStorage: () => handleSelfieStoragePermission(true),
        disagreeToSelfieStorage: () => handleSelfieStoragePermission(false),
      },
    }),
    [disabledOptionsSet, handleSelfieStoragePermission],
  );

  // when in trouble during the onboarding, we might reset a user's selfie so force them back
  if (!user?.selfieUrl) {
    return <Redirect to="Selfie" />;
  }

  // If `currentInterface` for the user is set then redirect to the interface screen
  if (currentInterface !== null) {
    return <Redirect to="Interface" />;
  }

  return (
    <onboardingContext.Provider value={contextValue}>
      <Router initialState={routerState} onStateChange={handleRouteChange}>
        {steps.map((step) => (
          <Route key={step.id} name={step.id} component={Step} defaultParams={step} />
        ))}
      </Router>
    </onboardingContext.Provider>
  );
}

// STEP
// ----

/** Implements going back by swiping/back button and displays the step */
export function Step({ params: step }: { params: Step }) {
  const router = useRouter();
  const [tapPosition, setTapPosition] = useState({ x: 0, y: 0 });

  const bind = useDrag(({ swipe: [swipeX], tap, xy: [x, y] }) => {
    if (swipeX === 1) {
      router.goBack();
    }

    if (tap && step.confetti) {
      setTapPosition({ x, y });
    }
  });

  useEffect(() => {
    // Don't trigger confetti on the first render
    if (!step.confetti || (tapPosition.x === 0 && tapPosition.y === 0)) {
      return;
    }

    confetti({
      origin: {
        x: tapPosition.x / window.innerWidth,
        y: tapPosition.y / window.innerHeight,
      },
    });
    // Don't trigger confetti on screen resize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tapPosition]);

  return (
    <div className="relative h-full touch-none" {...bind()}>
      <BackButton />

      {(() => {
        switch (step.type) {
          case "basic":
            return <BasicStep step={step} />;
          case "countdown":
            return <CountdownStep step={step} />;
          case "privacy-policy":
            return <PrivacyPolicyStep step={step} />;
          default:
            // @ts-expect-error Switch block should be exhaustive
            throw new Error(`Unknown step type '${step.type}'`);
        }
      })()}
    </div>
  );
}

const BackButton = memo(function BackButton() {
  const router = useRouter();

  if (router.canGoBack === false) {
    return null;
  }

  return (
    <Button
      aria-label="Back"
      onClick={router.goBack}
      variant="ghost"
      size="icon"
      className="absolute left-2 top-2 text-sm"
      disabled={!router.canGoBack}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        style={{ height: "1rem", width: "1rem" }}
        viewBox="0 0 24 24"
      >
        <path
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M5 12h14M5 12l6 6m-6-6l6-6"
        ></path>
      </svg>
    </Button>
  );
});

// BASIC STEP
// ----------

function BasicStep({ step }: { step: BasicStep }) {
  const { buttons, html, variant = "default", header } = step;

  const router = useRouter();
  const { disabledOptions, actions } = useContext(onboardingContext);
  const [isAnimating, setIsAnimating] = useState(true);

  const handleButtonClick = useCallback(
    ({
      target,
      text,
      canPickOnce,
      action,
    }: {
      target: string;
      text: string;
      canPickOnce: boolean;
      action?: string;
    }) =>
      () => {
        if (canPickOnce) {
          disabledOptions.add(step.id + text);
        }

        if (action) {
          const actionFunction = actions[action];

          if (typeof actionFunction !== "function") {
            throw new Error(`Unknown action '${action}'`);
          }

          actionFunction();
        }

        router.navigate(target);
      },
    [actions, disabledOptions, router, step.id],
  );

  const handleAnimationEnd = useCallback(() => {
    setIsAnimating(false);
  }, []);

  return (
    <Container
      className={cn(
        "flex flex-col gap-4 pt-10",
        variant === "primary" && "bg-primary text-primary-foreground",
      )}
    >
      {header && header.html && (
        <div className="flex flex-1 justify-center">
          <AppearingTextWithSound
            html={header.html}
            className={cn("my-auto", header.variant == "huge" ? "text-9xl" : "")}
          />
        </div>
      )}

      <div className="flex flex-1">
        <AppearingTextWithSound
          html={html}
          className="my-auto"
          onAnimationEnd={handleAnimationEnd}
        />
      </div>

      {buttons.map(({ text, canPickOnce = false, target = "$next", action }) => (
        <Button
          key={text}
          className={cn(
            "h-auto whitespace-normal transition-opacity duration-200 ease-out",
            isAnimating ? "pointer-events-none !opacity-0" : "pointer-events-auto opacity-100",
          )}
          variant={variant === "primary" ? "outline" : "default"}
          onClick={handleButtonClick({ target, text, canPickOnce, action })}
          disabled={canPickOnce && disabledOptions.has(step.id + text)}
        >
          {text}
        </Button>
      ))}
    </Container>
  );
}

// PRIVACY POLICY STEP
// -------------------

function PrivacyPolicyStep({ step }: { step: PrivacyPolicyStep }) {
  const { buttons, html } = step;

  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const { actions } = useContext(onboardingContext);
  const [canScroll, setCanScroll] = useState(true);
  const [isAnimating, setIsAnimating] = useState(true);

  const handleButtonClick = useCallback(
    ({ action }: { action?: string }) =>
      () => {
        if (action) {
          const actionFunction = actions[action];

          if (typeof actionFunction !== "function") {
            throw new Error(`Unknown action '${action}'`);
          }

          actionFunction();
        }

        router.navigate("$next");
      },
    [actions, router],
  );

  const handleAnimationEnd = useCallback(() => {
    setIsAnimating(false);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const handleScroll = () => {
      // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
      const { clientHeight, scrollHeight, scrollTop } = container;
      const hasScrolledToBottom = Math.abs(scrollHeight - clientHeight - scrollTop) <= 1;

      setCanScroll(!hasScrolledToBottom);
    };

    container.addEventListener("scroll", handleScroll);

    // We need a timeout to wait for the layout to update
    const timeout = setTimeout(handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <Container ref={containerRef} className="flex flex-col gap-4 pt-10">
      <div className="flex flex-1">
        <AppearingTextWithSound
          html={html}
          className="my-auto"
          onAnimationEnd={handleAnimationEnd}
        />
      </div>

      <div
        className={cn(
          "sticky bottom-0 -mt-4 flex flex-col gap-4 bg-background pt-4 transition-opacity duration-200 ease-out",
          isAnimating ? "pointer-events-none !opacity-0" : "pointer-events-auto opacity-100",
          canScroll &&
            "before:absolute before:bottom-full before:w-full before:border-b-2 before:border-muted-foreground before:bg-background before:text-center before:text-sm before:font-bold before:uppercase before:text-muted-foreground before:content-['scroll_down']",
        )}
      >
        {buttons.map(({ text, action }) => (
          <Button
            key={text}
            className="h-auto whitespace-normal"
            onClick={handleButtonClick({ action })}
            disabled={canScroll}
          >
            {text}
          </Button>
        ))}
      </div>
    </Container>
  );
}

// COUNTDOWN STEP
// --------------

function CountdownStep({ step }: { step: CountdownStep }) {
  const router = useRouter();
  const { countdownLength, target = "$next", variant = "default" } = step;
  const [isCounting, setIsCounting] = useState(false);

  const [, invisibleCountdown] = useCountdown(countdownLength, {
    startImmediately: false,
  });

  const onTextAppeared = useCallback(() => {
    if (variant === "default") {
      setIsCounting(true);
    } else {
      invisibleCountdown.start();
    }
  }, [invisibleCountdown, variant]);

  const onDone = useCallback(() => {
    router.navigate(target);
  }, [router, target]);

  useEffect(() => {
    if (invisibleCountdown.state === "done") {
      onDone();
    }
  }, [invisibleCountdown.state, onDone]);

  return (
    <Container className="flex flex-col gap-4">
      <div className="flex flex-1">
        <AppearingTextWithSound
          html={step.html}
          onAnimationEnd={onTextAppeared}
          className="my-auto"
        />
      </div>

      {variant === "default" && (
        <ProgressCountdown
          length={countdownLength}
          isRunning={isCounting}
          onDone={onDone}
          className={cn(
            "transition-opacity duration-200 ease-out",
            isCounting ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </Container>
  );
}
